import React, {
  useState, useMemo, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../../hooks';
import { areAllObligatoryMilestonePasses, getDaysLeft, getDate } from '../../helpers/timeline';
import Button from './Button';
import ViewMoreIcon from '../../../assets/simple-arrow-down.svg';
import ViewLessIcon from '../../../assets/simple-arrow-up.svg';
import TickIcon from '../../../assets/simple-tick.svg';
import LockIcon from '../../../assets/lock.svg';
import ClockIcon from '../../../assets/clock.svg';
import DividerIcon from '../../../assets/divider.svg';
import InfoIcon from '../../../assets/info-icon.svg';
import VerticalLine from '../../../assets/arrow-right-v2.svg';

import styles from '../../styles/views/components/MilestoneItem.module.scss';

let timeoutId;

const MilestoneItem = ({
  milestone,
  isUserEditorOrAdmin,
  currentIndex,
  setCurrentindex,
  isFirstMilestone,
  isLastItem,
  projectType,
}) => {
  const milestoneRef = useRef();

  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [isViewMode, setViewMode] = useState(false);

  const isMilestoneCompleted = useMemo(() => areAllObligatoryMilestonePasses(milestone.steps), [milestone]);
  const milestoneDaysLeft = useMemo(() => getDaysLeft(milestone.deadline), [milestone]);
  const isActionRequired = useMemo(() => milestone.isActionRequired, [milestone]);

  useEffect(() => {
    if (milestone.isCurrentMilestone && !isUserEditorOrAdmin) {
      timeoutId = setTimeout(() => milestoneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 1500);
    }
    return () => clearTimeout(timeoutId);
  }, [milestone.isCurrentMilestone, isUserEditorOrAdmin]);

  useEffect(() => {
    if (currentIndex !== milestone.index) setViewMode(false);
  }, [currentIndex, milestone.index]);

  useEffect(() => {
    if (milestone.isCurrentMilestone) setViewMode(true);
  }, [milestone.isCurrentMilestone]);

  const normalizedDate = (date, isDeadlineDate = false) => {
    const { day, month, year } = getDate(date);
    if (!isDeadlineDate) return `${day} ${month} ${year}`;
    if (isMilestoneCompleted || milestoneDaysLeft >= 7) return `to ${month} ${day}, ${year}`;
    if (milestoneDaysLeft >= 0) return t('milestoneSection.daysLeft', { ns: i18next.Namespace.COMMON, days: Math.abs(milestoneDaysLeft) });
    return t('milestoneSection.daysOverdue', { ns: i18next.Namespace.COMMON, days: Math.abs(milestoneDaysLeft) });
  };

  const openViewMode = (e) => {
    e.stopPropagation();
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    setCurrentindex(milestone.index);
    setViewMode(true);
  };

  const closeViewMode = (e) => {
    e.stopPropagation();
    setViewMode(false);
  };

  const goToCurrentStep = (e) => {
    e.stopPropagation();
    navigate(milestone.goToStep.path);
  };

  const renderStatusBlock = () => {
    const { isCurrentMilestone, isLocked } = milestone;

    if (isLocked) return <div className={`${isLastItem ? styles.lastItem : ''} ${styles.statusBlock}`}><span className={styles.locked}><LockIcon /></span></div>;

    return (
      <div className={`${isLastItem ? styles.lastItem : ''} ${styles.statusBlock}`}>
        {isMilestoneCompleted && <span className={styles.completedIcon}><TickIcon /></span>}
        {isCurrentMilestone && !isActionRequired && <span className={styles.currentNotActionRequired}><VerticalLine /></span>}
        {isCurrentMilestone && isActionRequired && <span className={styles.currentActionRequired}><VerticalLine /></span>}
        {!isCurrentMilestone && isActionRequired && <span className={styles.actionRequired}><VerticalLine /></span>}
      </div>
    );
  };

  const getMilestoneStatusLabel = () => {
    const { isCurrentMilestone, isLocked } = milestone;
    let milestoneStatus = '';
    if (isMilestoneCompleted) milestoneStatus = 'completed';
    if (isCurrentMilestone && !isActionRequired) milestoneStatus = 'inProgress';
    if (isCurrentMilestone && isActionRequired) milestoneStatus = 'actionRequired';
    if (!isCurrentMilestone && isActionRequired) milestoneStatus = 'actionRequired';
    if (isLocked || !milestoneStatus) milestoneStatus = 'comingSoon';
    return <span className={`${styles.statusLabel} ${styles[milestoneStatus]}`}>{t(`timelineSection.${milestoneStatus}`, { ns: i18next.Namespace.COMMON })}</span>;
  };

  const renderGoToPageButton = () => (milestone.goToStep && !isUserEditorOrAdmin && (
    <Button
      value={t('goToPageButton', { ns: i18next.Namespace.COMMON })}
      color="gold"
      variant="secondary"
      isDisabled={milestone.isLocked}
      onClick={goToCurrentStep}
      extraStyles={{ marginRight: '16px', height: isMobile ? '32px' : '' }}
    />
  ));

  const milestoneTimeStyles = useMemo(() => {
    if (isMilestoneCompleted || milestoneDaysLeft >= 7) return '';
    if (milestoneDaysLeft >= 0) return styles.warningOrange;
    return styles.warningRed;
  }, [isMilestoneCompleted, milestoneDaysLeft]);

  return (
    <div className={styles.container} ref={milestoneRef}>
      {renderStatusBlock()}
      <div className={`${styles.content} ${(isViewMode && milestone.isCurrentMilestone) ? styles.viewMode : ''}`}>
        {getMilestoneStatusLabel()}
        <div
          className={[isUserEditorOrAdmin ? styles.small : '', styles.titleSection].join(' ')}
          onClick={isViewMode ? closeViewMode : openViewMode}
        >
          <div className={`${styles.titleWrapper} ${(milestone.isCurrentMilestone || isActionRequired) ? styles.inProgressStatus : ''}`}>
            <span
              data-tip={isUserEditorOrAdmin ? t(`milestoneSection.stepsInfoMessage.${milestone.title}`, { ns: i18next.Namespace.COMMON }) : ''}
            >
              {t(`timelineSection.${milestone.title}`, { ns: i18next.Namespace.COMMON })}
            </span>
            {!isFirstMilestone
              ? (
                <div className={`${styles.deadline} ${milestoneTimeStyles}`}>
                  {!isMobile && <DividerIcon />}
                  <ClockIcon className={styles.clock} />
                  <span className={[isUserEditorOrAdmin ? styles.small : '', styles.time].join(' ')}>{normalizedDate(milestone.deadline, true)}</span>
                </div>
              ) : null}
          </div>
          <div className={styles.navigation}>
            {!isMobile && renderGoToPageButton()}
            <span className={styles.modeButton}>
              {isViewMode ? <ViewLessIcon /> : <ViewMoreIcon />}
            </span>
          </div>
        </div>
        {isMobile && renderGoToPageButton()}
        {isViewMode && (
          <div className={styles.milestoneDetails}>
            {!isUserEditorOrAdmin && (
            <div className={styles.infoMessageWrapper}>
              <div className={styles.infoIcon}>
                <InfoIcon />
              </div>
              <div className={styles.message}>
                {t(`milestoneSection.stepsInfoMessage.${milestone.title}`, { ns: i18next.Namespace.COMMON })}
                {milestone.videoHref && (
                <a href={milestone.videoHref} target="_blank" rel="noopener noreferrer">
                  {t('milestoneSection.stepsInfoMessage.watchVideo', { ns: i18next.Namespace.COMMON })}
                </a>
                )}
              </div>
            </div>
            )}
            <div className={`${styles.steps} ${milestone.isCurrentMilestone ? styles.currentStep : ''}`}>
              {milestone.steps.map((step) => {
                if (!step.showFor.includes(projectType)) return null;
                return (
                  <div className={styles.stepItem} key={step.name}>
                    <div className={step.isPassed ? styles.passed : styles.noPassed}>
                      {step.isPassed ? <TickIcon /> : <span className={styles.emptyCheckbox} />}
                      <span>{t(`milestoneSection.steps.${milestone.title}.${step.name}`, { ns: i18next.Namespace.COMMON })}</span>
                    </div>
                    <div className={styles.date}>
                      <span className={styles.separator} />
                      <span>{step.date ? normalizedDate(step.date) : '--'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

MilestoneItem.propTypes = {
  milestone: PropTypes.object.isRequired,
  isUserEditorOrAdmin: PropTypes.bool,
  currentIndex: PropTypes.number.isRequired,
  setCurrentindex: PropTypes.func.isRequired,
  isFirstMilestone: PropTypes.bool,
  isLastItem: PropTypes.bool,
  projectType: PropTypes.string.isRequired,
};

MilestoneItem.defaultProps = {
  isFirstMilestone: false,
  isLastItem: false,
  isUserEditorOrAdmin: false,
};

export default MilestoneItem;
